import { useForm, Controller } from 'react-hook-form';

// import Page from "components/Page"; // adjust path if needed
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Box
} from '@mui/material';
import { apiPostUnsecure } from 'src/utils/apis';
import { apiUrl } from 'src/utils/apis';
import { encryptText } from 'src/utils/secrete';


const reasons = [
  'I am exercising my legal right to request permanent deletion of my account and associated personal data.',
  'I withdraw my consent to the processing of my personal information and request account removal.',
  'I request deletion of my account in accordance with applicable data protection and privacy laws.',
  'I no longer consent to the collection, storage, or processing of my personal data.',
  'I wish to terminate my agreement with the service provider and request full data erasure.',
  'I have concerns regarding compliance with data protection obligations.',
  'I request removal of all personal information associated with my account.',
  'I am exercising my right to erasure under relevant privacy regulations.',
];

type DeleteAccountFormValues = {
  reason: string;
  message: string;
  email: string;
  password: string;
};

function DeleteAccount() {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DeleteAccountFormValues>({
    defaultValues: {
      reason: '',
      message: '',
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: DeleteAccountFormValues) => {
    const encryptedData = {
      data: encryptText(JSON.stringify(data)),
    };

    apiPostUnsecure(
      apiUrl.delete_account,
      encryptedData,
      (res: any) => {
        console.log('Account deleted successfully', res);
        alert('Account deleted successfully');

        // ✅ Reset form after success
        reset();
      },
      (err: any) => {
        console.error('Delete failed:', err);
        alert(err);
      }
    );
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" mt={5}>
      <Card sx={{ maxWidth: 500, width: '100%', p: 2 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Delete Your Account
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            This action is permanent. Please confirm your details before deleting your account.
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Reason */}
            <Controller
              name="reason"
              control={control}
              rules={{ required: 'Please select a reason.' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Select Reason"
                  margin="normal"
                  error={!!errors.reason}
                  helperText={errors.reason?.message}
                >
                  {reasons.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Message */}
            <Controller
              name="message"
              control={control}
              rules={{ required: 'Message is required.' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  label="Message"
                  margin="normal"
                  error={!!errors.message}
                  helperText={errors.message?.message}
                />
              )}
            />

            {/* Email */}
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address.',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  label="Email"
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />

            {/* Password */}
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Password is required.' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="password"
                  label="Password"
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              )}
            />

            <Typography variant="caption" color="error" display="block" mt={2}>
              ⚠ This action cannot be undone.
            </Typography>

            <Button
              type="submit"
              variant="contained"
              color="error"
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default DeleteAccount;